import React, { useState, useEffect } from 'react';

// A simple tooltip icon component
const Tooltip = ({ text }) => (
  <span className="tooltip">
    ?
    <span className="tooltip-text">{text}</span>
  </span>
);

// Main Pricing Tool Component
export const BalloonArtistPricingTool = () => {
  // ... (State hooks) ...
  const [customerInfo, setCustomerInfo] = useState({
    customer: '',
    contact: '',
    address: '',
    email: '',
    phone: '',
    jobNo: '',
    eventDate: '',
    notes: '',
  });

  const [materials, setMaterials] = useState([
    { name: '', packSize: '', costPerPack: '', itemsUsed: '' },
  ]);

  // CHANGED: labour -> labor
  const [labor, setLabor] = useState({
    perHour: 30,
    minutes: 30,
  });

  const [otherExpenses, setOtherExpenses] = useState([
    { name: '', cost: '' },
  ]);

  const [percentages, setPercentages] = useState({
    overheads: 10,
    profit: 30,
  });

  const [actualSellingPrice, setActualSellingPrice] = useState(0);

  const [calculated, setCalculated] = useState({
    materialCosts: [],
    totalMaterialCost: 0,
    costPerMinute: 0,
    totalLaborCost: 0, // CHANGED: totalLabourCost -> totalLaborCost
    totalOtherExpenses: 0,
    totalDirectCosts: 0,
    overheadsValue: 0,
    profitValue: 0,
    sellingPrice: 0,
  });

  useEffect(() => {
    // --- Material Calculations ---
    const newMaterialCosts = materials.map((mat) => {
      const packSize = parseFloat(mat.packSize) || 0;
      const costPerPack = parseFloat(mat.costPerPack) || 0;
      const itemsUsed = parseFloat(mat.itemsUsed) || 0;
      if (packSize > 0) {
        return (costPerPack / packSize) * itemsUsed;
      }
      return 0;
    });
    const totalMaterialCost = newMaterialCosts.reduce((sum, cost) => sum + cost, 0);

    // --- Labor Calculations --- // CHANGED: Labour -> Labor
    const perHour = parseFloat(labor.perHour) || 0; // CHANGED: labour -> labor
    const minutes = parseFloat(labor.minutes) || 0; // CHANGED: labour -> labor
    const costPerMinute = perHour / 60;
    const totalLaborCost = costPerMinute * minutes; // CHANGED: totalLabourCost -> totalLaborCost

    // --- Other Expenses Calculations ---
    const totalOtherExpenses = otherExpenses.reduce(
      (sum, exp) => sum + (parseFloat(exp.cost) || 0),
      0
    );

    // --- Summary Calculations ---
    const totalDirectCosts = totalMaterialCost + totalLaborCost + totalOtherExpenses; // CHANGED
    const overheadsPercent = parseFloat(percentages.overheads) || 0;
    const profitPercent = parseFloat(percentages.profit) || 0;

    const overheadsValue = totalDirectCosts * (overheadsPercent / 100);
    const profitValue = (totalDirectCosts + overheadsValue) * (profitPercent / 100);
    const sellingPrice = totalDirectCosts + overheadsValue + profitValue;

    setCalculated({
      materialCosts: newMaterialCosts,
      totalMaterialCost,
      costPerMinute,
      totalLaborCost, // CHANGED: totalLabourCost -> totalLaborCost
      totalOtherExpenses,
      totalDirectCosts,
      overheadsValue,
      profitValue,
      sellingPrice,
    });

    setActualSellingPrice(sellingPrice);
  }, [materials, labor, otherExpenses, percentages]); // CHANGED: labour -> labor

  const handleDynamicChange = (setter, index, field, value) => {
    setter((prev) => {
      const newItems = [...prev];
      newItems[index][field] = value;
      return newItems;
    });
  };

  const addRow = (setter, newRow) => setter((prev) => [...prev, newRow]);
  const removeRow = (setter, index) =>
    setter((prev) => prev.filter((_, i) => i !== index));

  const tooltips = {
    customer: 'The name of the client or company.',
    jobNo: 'A unique number to identify this job (e.g., invoice number).',
    materials: 'List each type of balloon or supply used.',
    packSize: 'How many items came in the pack you purchased?',
    costPerPack: 'What was the total cost for that pack?',
    itemsUsed: 'How many individual items did you use for this job?',
    // CHANGED: labour -> labor
    laborPerHour: 'Your standard hourly rate for labor.',
    minutes: 'Total minutes spent creating the arrangement.',
    costPerMinute: 'Calculated cost per minute (Hourly Rate / 60).',
    otherExpenses: 'Any other costs like travel, rentals, or special equipment.',
    totalDirectCosts:
      'The sum of all your material, labor, and other direct expenses.', // CHANGED
    overheads:
      'Percentage to cover indirect business costs (rent, insurance, marketing).',
    profit: 'The percentage of profit you want to make on top of all costs.',
    sellingPrice:
      'The calculated price based on all costs, overheads, and profit.',
    actualSellingPrice:
      'The final price you quote to the customer. You can adjust this from the calculated price.',
    wowFactor:
      'Consider the value and "WOW" factor. Does the final piece have a high perceived value that might justify a higher price?',
  };

  return (
    <>
      <style>{`
        /* ... (CSS styles remain unchanged) ... */
        :root {
          --input-bg: #fff7d6;
          --calculated-bg: #e0f7fa;
          --header-bg: #f1f3f4;
          --border-color: #ccc;
          --primary-text: #333;
          --section-bg: #ffffff;
          --button-bg: #4caf50;
          --button-remove-bg: #f44336;
          --button-text: white;
          --shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .pricing-tool-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
          max-width: 1100px;
          margin: 2rem auto;
          padding: 1rem;
          color: var(--primary-text);
        }
        .pricing-tool-container h1, .pricing-tool-container h2 {
          text-align: center;
          color: var(--primary-text);
          border-bottom: 2px solid var(--border-color);
          padding-bottom: 0.5rem;
          margin-bottom: 1.5rem;
        }
        .section {
          background: var(--section-bg);
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 1.5rem;
          box-shadow: var(--shadow);
        }
        .grid { display: grid; grid-template-columns: 1fr; gap: 1rem; }
        .full-width { grid-column: 1 / -1; }
        .form-group { display: flex; flex-direction: column; }
        .form-group label {
          margin-bottom: 0.5rem;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .input-field, .calculated-field {
          padding: 0.75rem;
          border: 1px solid var(--border-color);
          border-radius: 4px;
          font-size: 1rem;
          width: 100%;
          box-sizing: border-box;
        }
        .input-field { background-color: var(--input-bg); }
        .calculated-field { background-color: var(--calculated-bg); font-weight: bold; }
        .dynamic-table { width: 100%; border-collapse: collapse; margin-top: 1rem; }
        .dynamic-table th, .dynamic-table td {
          border: 1px solid var(--border-color);
          padding: 0.75rem;
          text-align: left;
        }
        .dynamic-table th { background-color: var(--header-bg); }
        .dynamic-table .input-field { min-width: 60px; }
        .dynamic-table .calculated-field { text-align: right; }
        .action-cell { width: 40px; }
        .total-row td { font-weight: bold; }
        .total-row .calculated-field { text-align: right; }
        
        .materials-footer-actions {
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }
        .materials-footer-actions .add-row-btn {
          margin: 0;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: 1fr 100px 150px;
          align-items: center;
          gap: 0.5rem 1rem;
          margin-top: 1rem;
        }
        .summary-grid > label { font-weight: 500; text-align: right; }
        .summary-grid .input-field, .summary-grid .calculated-field { text-align: right; }
        .final-price { font-size: 1.5rem; font-weight: bold; }
        .final-price label { text-align: right; }
        .final-price .input-field, .final-price .calculated-field {
          background: var(--input-bg);
          font-size: 1.5rem;
        }
        .add-row-btn, .remove-row-btn {
          border: none;
          color: var(--button-text);
          border-radius: 50%;
          cursor: pointer;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: transform 0.2s;
        }
        .add-row-btn { background-color: var(--button-bg); width: 30px; height: 30px; font-size: 1.2rem; margin-top: 0.5rem;}
        .remove-row-btn { background-color: var(--button-remove-bg); width: 24px; height: 24px; font-size: 1rem; }
        .add-row-btn:hover, .remove-row-btn:hover { transform: scale(1.1); }
        .tooltip {
          position: relative;
          cursor: help;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background-color: #a0aec0;
          color: white;
          font-size: 12px;
          font-weight: bold;
          flex-shrink: 0;
        }
        .tooltip .tooltip-text {
          visibility: hidden;
          width: 220px;
          background-color: #2d3748;
          color: #fff;
          text-align: center;
          border-radius: 6px;
          padding: 8px;
          position: absolute;
          z-index: 1;
          bottom: 125%;
          left: 50%;
          margin-left: -110px;
          opacity: 0;
          transition: opacity 0.3s;
          font-size: 12px;
          font-weight: normal;
        }
        .tooltip:hover .tooltip-text {
          visibility: visible;
          opacity: 1;
        }

        @media (min-width: 768px) {
          .grid {
            grid-template-columns: 1fr 1fr;
          }
          .dynamic-table td:first-child {
            width: 40%;
          }
        }
      `}</style>

      <div className="pricing-tool-container">
        <h1>Balloon Artist Pricing Tool</h1>

        {/* --- Customer & Job Info --- */}
        <section className="section">
          <h2>Job Information</h2>
          <div className="grid">
            {Object.keys(customerInfo).map((key) => (
              <div
                className={`form-group ${['address', 'notes'].includes(key) ? 'full-width' : ''}`}
                key={key}
              >
                <label>
                  {key.charAt(0).toUpperCase() +
                    key.slice(1).replace(/([A-Z])/g, ' $1')}
                  <Tooltip text={tooltips[key]} />
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={customerInfo[key]}
                  onChange={(e) =>
                    setCustomerInfo({ ...customerInfo, [key]: e.target.value })
                  }
                />
              </div>
            ))}
          </div>
        </section>

        {/* --- Materials Section --- */}
        <section className="section">
          <h2>Materials</h2>
          <table className="dynamic-table">
            <thead>
              <tr>
                <th>
                  Material <Tooltip text={tooltips.materials} />
                </th>
                <th>
                  Pack Size <Tooltip text={tooltips.packSize} />
                </th>
                <th>
                  Cost/Pack <Tooltip text={tooltips.costPerPack} />
                </th>
                <th>
                  # Used <Tooltip text={tooltips.itemsUsed} />
                </th>
                <th>Total Cost</th>
                <th className="action-cell"></th>
              </tr>
            </thead>
            <tbody>
              {materials.map((mat, index) => (
                <tr key={index}>
                  <td><input type="text" className="input-field" placeholder="e.g., 11-inch latex balloons" value={mat.name} onChange={(e) => handleDynamicChange(setMaterials, index, 'name', e.target.value)} /></td>
                  <td><input type="number" className="input-field" placeholder="100" value={mat.packSize} onChange={(e) => handleDynamicChange(setMaterials, index, 'packSize', e.target.value)} /></td>
                  <td><input type="number" className="input-field" placeholder="15.00" value={mat.costPerPack} onChange={(e) => handleDynamicChange(setMaterials, index, 'costPerPack', e.target.value)} /></td>
                  <td><input type="number" className="input-field" placeholder="50" value={mat.itemsUsed} onChange={(e) => handleDynamicChange(setMaterials, index, 'itemsUsed', e.target.value)} /></td>
                  <td><div className="calculated-field">{calculated.materialCosts[index]?.toFixed(2) || '0.00'}</div></td>
                  <td className="action-cell">{materials.length > 1 && (<button className="remove-row-btn" onClick={() => removeRow(setMaterials, index)}>-</button>)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="total-row">
                <td className="materials-footer-actions"><button className="add-row-btn" onClick={() => addRow(setMaterials, { name: '', packSize: '', costPerPack: '', itemsUsed: '' })}>+</button></td>
                <td colSpan="3" style={{ textAlign: 'right' }}>Total Materials Cost</td>
                <td colSpan="2"><div className="calculated-field">{calculated.totalMaterialCost.toFixed(2)}</div></td>
              </tr>
            </tfoot>
          </table>
        </section>

        {/* --- Labor & Other Expenses --- */}
        <div className="grid">
          <section className="section">
            <h2>Labor</h2> {/* CHANGED */}
            <div className="form-group">
              <label>
                Labor (Per hour) <Tooltip text={tooltips.laborPerHour} /> {/* CHANGED */}
              </label>
              <input type="number" className="input-field" value={labor.perHour} onChange={(e) => setLabor({ ...labor, perHour: e.target.value })} /> {/* CHANGED */}
            </div>
            <div className="form-group">
              <label>
                Minutes <Tooltip text={tooltips.minutes} />
              </label>
              <input type="number" className="input-field" value={labor.minutes} onChange={(e) => setLabor({ ...labor, minutes: e.target.value })} /> {/* CHANGED */}
            </div>
            <div className="form-group">
              <label>
                Cost Per Minute <Tooltip text={tooltips.costPerMinute} />
              </label>
              <div className="calculated-field">{calculated.costPerMinute.toFixed(2)}</div>
            </div>
            <div className="form-group">
              <label>Total Labor Cost</label> {/* CHANGED */}
              <div className="calculated-field">{calculated.totalLaborCost.toFixed(2)}</div> {/* CHANGED */}
            </div>
          </section>

          <section className="section">
            <h2>Other Expenses <Tooltip text={tooltips.otherExpenses} /></h2>
            <table className="dynamic-table">
              <thead>
                <tr>
                  <th>Expense</th>
                  <th>Cost</th>
                  <th className="action-cell"></th>
                </tr>
              </thead>
              <tbody>
                {otherExpenses.map((exp, index) => (
                  <tr key={index}>
                    <td><input type="text" className="input-field" placeholder="e.g., Travel" value={exp.name} onChange={(e) => handleDynamicChange(setOtherExpenses, index, 'name', e.target.value)} /></td>
                    <td><input type="number" className="input-field" placeholder="20.00" value={exp.cost} onChange={(e) => handleDynamicChange(setOtherExpenses, index, 'cost', e.target.value)} /></td>
                    <td className="action-cell">{otherExpenses.length > 1 && (<button className="remove-row-btn" onClick={() => removeRow(setOtherExpenses, index)}>-</button>)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td style={{ textAlign: 'right' }}>Total Other Expenses</td>
                  <td colSpan="2"><div className="calculated-field">{calculated.totalOtherExpenses.toFixed(2)}</div></td>
                </tr>
              </tbody>
            </table>
            <button className="add-row-btn" onClick={() => addRow(setOtherExpenses, { name: '', cost: '' })}>+</button>
          </section>
        </div>

        {/* --- Final Summary --- */}
        <section className="section">
          <h2>Summary</h2>
          <div className="summary-grid">
            <label>Total Direct Costs <Tooltip text={tooltips.totalDirectCosts} /></label>
            <span></span>
            <div className="calculated-field">{calculated.totalDirectCosts.toFixed(2)}</div>

            <label>Overheads <Tooltip text={tooltips.overheads} /></label>
            <input type="number" className="input-field" value={percentages.overheads} onChange={(e) => setPercentages({ ...percentages, overheads: e.target.value })} />
            <div className="calculated-field">{calculated.overheadsValue.toFixed(2)}</div>

            <label>Profit <Tooltip text={tooltips.profit} /></label>
            <input type="number" className="input-field" value={percentages.profit} onChange={(e) => setPercentages({ ...percentages, profit: e.target.value })} />
            <div className="calculated-field">{calculated.profitValue.toFixed(2)}</div>
          </div>
          <hr style={{ margin: '1.5rem 0', border: '1px solid #eee' }} />
          <div className="summary-grid final-price">
            <label>Selling Price <Tooltip text={tooltips.sellingPrice} /></label>
            <span></span>
            <div className="calculated-field">{calculated.sellingPrice.toFixed(2)}</div>

            <label>Actual Selling Price <Tooltip text={tooltips.actualSellingPrice} /></label>
            <span></span>
            <input type="number" className="input-field" value={actualSellingPrice.toFixed(2)} onChange={(e) => setActualSellingPrice(parseFloat(e.target.value))} />
          </div>
          <div className="form-group full-width" style={{ marginTop: '1.5rem' }}>
            <label>Value to your customer? What's the WOW factor? <Tooltip text={tooltips.wowFactor} /></label>
            <textarea className="input-field" rows="3" placeholder="e.g., This is a large, custom-themed arch that will be the centerpiece of the event..."></textarea>
          </div>
        </section>
      </div>
    </>
  );
};